import SurveyButton from '@/components/Base/SurveyButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import {
  SurveyContentPanelInset,
  SurveyFormPanel,
  surveyQuestionTextClass,
} from '@/components/layout/SurveyContentPanel';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import {
  getOtherPrevBrandReasonKeys,
  readOtherPrevBrandAnswerAnswers,
  writeOtherPrevBrandAnswerAnswers,
  type OtherPrevBrandAnswerAnswers,
  type OtherPrevBrandReasonKey,
} from '@/lib/otherPrevBrandAnswerStorage';
import { isOtherPrevBrandComplete, readOtherPrevBrandAnswers } from '@/lib/otherPrevBrandStorage';
import {
  isOtherProductTypeComplete,
  readOtherProductTypeAnswers,
} from '@/lib/otherProductTypeStorage';
import { readPrevBrandAnswers } from '@/lib/prevBrandStorage';
import { surveyChoiceInputClass } from '@/lib/surveyChoiceInput';
import { surveyBeforeOtherProductRedirect, surveyProfileRedirect, surveyQuestionnaireRedirect } from '@/lib/surveyFlow';
import { useAppSelector } from '@/stores/hooks';
import { selectOtpDraft, selectPdpaConsent, selectSurveyScreening } from '@/stores/selectors';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

const labelClass = 'text-left text-[15px] font-medium leading-tight text-black sm:text-[16px]';

const hintClass = 'm-0 text-left text-[14px] leading-tight text-black/80 sm:text-[15px]';

const sliderClass =
  'h-2 w-full cursor-pointer appearance-none rounded-full bg-green-600 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-black [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-black';

function ReasonOption({
  id,
  label,
  checked,
  onToggle,
}: {
  id: string;
  label: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <label htmlFor={id} className="flex cursor-pointer items-start gap-1.5">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className={surveyChoiceInputClass}
      />
      <span className={labelClass}>{label}</span>
    </label>
  );
}

function formatBrandVariantLabel(brand?: string, variant?: string): string | null {
  const b = brand?.trim();
  const v = variant?.trim();
  if (!b || !v) return null;
  return `${b} - ${v}`;
}

export default function PrevBrandAnswerOther() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const { customerUuid, otpVerified, questionnaireSet } = useAppSelector(selectOtpDraft);

  const [answers, setAnswers] = useState<OtherPrevBrandAnswerAnswers>(() =>
    readOtherPrevBrandAnswerAnswers()
  );
  const [warning, setWarning] = useState<string | null>(null);

  useEffect(() => {
    writeOtherPrevBrandAnswerAnswers(answers);
  }, [answers]);

  if (surveyLanguage == null) return <Navigate to="/" replace />;
  if (surveyScreening == null) return <Navigate to="/age_smoker" replace />;
  if (!pdpaConsent) return <Navigate to="/pdpa" replace />;
  if (!customerUuid || !otpVerified) return <Navigate to="/phone" replace />;
  const questionnaireRedirect = surveyQuestionnaireRedirect(questionnaireSet);
  if (questionnaireRedirect) return <Navigate to={questionnaireRedirect} replace />;
  const profileRedirect = surveyProfileRedirect(questionnaireSet);
  if (profileRedirect) return <Navigate to={profileRedirect} replace />;
  const beforeOtherRedirect =
    questionnaireSet === 'A' || questionnaireSet === 'B'
      ? surveyBeforeOtherProductRedirect(questionnaireSet)
      : '/survey';
  if (beforeOtherRedirect) return <Navigate to={beforeOtherRedirect} replace />;
  if (!isOtherProductTypeComplete() || !isOtherPrevBrandComplete()) {
    return <Navigate to="/survey/prevbrand-other" replace />;
  }

  const category = readOtherProductTypeAnswers().category;
  const reasonKeys = getOtherPrevBrandReasonKeys(category);
  const isVapeOrRrp = category === 'VAPE' || category === 'RRP';

  const reasonLabel = (key: OtherPrevBrandReasonKey): string => {
    if (key === 'offerMoreFlavours') return t.prevBrandAnswerOther.offerMoreFlavours;
    if (key === 'moreConsiderate') return t.prevBrandAnswerOther.moreConsiderate;
    if (key === 'potentialToReduceRisks') return t.prevBrandAnswerOther.potentialToReduceRisks;
    if (key === 'recommendedByOthers') return t.prevBrandAnswer.recommendedByOthers;
    if (key === 'brandImage') return t.prevBrandAnswer.brandImage;
    if (key === 'productQuality') return t.prevBrandAnswer.productQuality;
    if (key === 'productFeatures') return t.prevBrandAnswer.productFeatures;
    if (key === 'price') return t.prevBrandAnswer.price;
    return t.prevBrandAnswer.taste;
  };

  const firstProductLabel =
    formatBrandVariantLabel(readPrevBrandAnswers()?.brand, readPrevBrandAnswers()?.variant) ??
    t.prevBrandAnswerOther.firstProduct;
  const secondProductLabel = isVapeOrRrp
    ? t.otherProductType[category === 'VAPE' ? 'vape' : 'rrp']
    : formatBrandVariantLabel(
        readOtherPrevBrandAnswers()?.brand,
        readOtherPrevBrandAnswers()?.variant
      ) ?? t.prevBrandAnswerOther.secondProduct;

  const secondProductCount = 10 - answers.firstProductCount;

  const toggleReason = (key: OtherPrevBrandReasonKey) => {
    setAnswers((prev) => ({
      ...prev,
      [key]: {
        checked: !prev[key].checked,
        elaboration: prev[key].elaboration,
      },
    }));
    setWarning(null);
  };

  const handleSmokeFrequencyChange = (firstProductCount: number) => {
    setAnswers((prev) => ({
      ...prev,
      firstProductCount,
      smokeFrequencyAdjusted: true,
    }));
    setWarning(null);
  };

  const handleContinue = () => {
    const hasReason = reasonKeys.some((key) => answers[key].checked);

    if (!hasReason) {
      setWarning(t.prevBrandAnswer.reasonRequiredWarning);
      return;
    }
    if (!answers.smokeFrequencyAdjusted) {
      setWarning(t.prevBrandAnswerOther.smokeFrequencyWarning);
      return;
    }

    writeOtherPrevBrandAnswerAnswers(answers);
    setWarning(null);
    navigate(questionnaireSet === 'B' ? '/likelyld' : '/whyldnow');
  };

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex h-[100dvh] max-h-[100dvh] flex-col pb-4">
        <div className="mx-auto flex min-h-0 w-full max-w-full flex-1 flex-col pt-[9dvh]">
          <SurveyContentPanelInset className="min-h-0 flex-1 overflow-y-auto">
            <div className="flex flex-col gap-3 pb-4">
              <SurveyFormPanel className="flex flex-col gap-2">
                <p className={surveyQuestionTextClass}>{t.prevBrandAnswer.whyQuestion}</p>
                <p className={hintClass}>{t.prevBrandAnswerOther.selectAll}</p>

                {reasonKeys.map((key) => (
                  <ReasonOption
                    key={key}
                    id={`other-reason-${key}`}
                    label={reasonLabel(key)}
                    checked={answers[key].checked}
                    onToggle={() => toggleReason(key)}
                  />
                ))}
              </SurveyFormPanel>

              <SurveyFormPanel className="flex flex-col gap-3">
                <p className={surveyQuestionTextClass}>{t.prevBrandAnswerOther.smokeFrequencyQuestion}</p>

                <div className="flex justify-between gap-3">
                  <span className="max-w-[48%] text-left text-[15px] font-semibold leading-tight text-black sm:text-[16px]">
                    {firstProductLabel}
                  </span>
                  <span className="max-w-[48%] text-right text-[15px] font-semibold leading-tight text-black sm:text-[16px]">
                    {secondProductLabel}
                  </span>
                </div>

                <input
                  id="smoke-frequency-slider"
                  type="range"
                  min={0}
                  max={10}
                  step={1}
                  value={answers.firstProductCount}
                  onChange={(e) => handleSmokeFrequencyChange(Number(e.target.value))}
                  onInput={(e) => handleSmokeFrequencyChange(Number(e.currentTarget.value))}
                  className={sliderClass}
                  aria-valuemin={0}
                  aria-valuemax={10}
                  aria-valuenow={answers.firstProductCount}
                  aria-label={t.prevBrandAnswerOther.smokeFrequencyQuestion}
                />

                <div className="flex justify-between gap-2">
                  <span className="text-[15px] font-medium text-black sm:text-[16px]">
                    {answers.smokeFrequencyAdjusted
                      ? `${answers.firstProductCount} ${t.prevBrandAnswerOther.times}`
                      : '—'}
                  </span>
                  <span className="text-[15px] font-medium text-black sm:text-[16px]">
                    {answers.smokeFrequencyAdjusted
                      ? `${secondProductCount} ${t.prevBrandAnswerOther.times}`
                      : '—'}
                  </span>
                </div>
              </SurveyFormPanel>
            </div>
          </SurveyContentPanelInset>

          <SurveyContentPanelInset className="mt-2 shrink-0">
            <div className="flex flex-col items-center gap-3">
              {warning != null ? <SurveyValidationAlert>{warning}</SurveyValidationAlert> : null}
              <SurveyButton className="w-full" onClick={handleContinue}>
                {t.prevBrandAnswer.continue}
              </SurveyButton>
            </div>
          </SurveyContentPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
